// The account's data sources grouped by the kind of system they are — orders
// (ERP / OMS / shops), warehouse (WMS), transport (TMS / tracking) — each with
// the moment it is read in the ticket journey, what it is looked up with, and
// whether an AI model is involved. Built-in sources (Ship24, the account's
// orders, Spacefill, Envoi du Net) are listed too: nothing reads a ticket
// without the page saying so. Backend: GET /api/setup/journey → sources.

import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaBoxes, FaPlus, FaShoppingCart, FaTruck, FaPuzzlePiece } from 'react-icons/fa';
import './Journey.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

export const CATEGORIES = [
    { key: 'orders', icon: FaShoppingCart, title: 'Commandes — ERP, OMS, boutiques', hint: 'La commande, ses produits et ses colis : lus avec la référence de commande, le numéro de suivi ou l’e-mail du client.' },
    { key: 'warehouse', icon: FaBoxes, title: 'Entrepôt — WMS', hint: 'Préparation, stock et expédition : lus avec la référence de commande ou le numéro de suivi.' },
    { key: 'transport', icon: FaTruck, title: 'Transport et suivi — TMS, API transporteur', hint: 'Le parcours du colis : lu avec le numéro de suivi.' },
    { key: 'other', icon: FaPuzzlePiece, title: 'Autres systèmes', hint: 'Les sources qui ne rentrent dans aucune des familles ci-dessus.' },
];

const WHEN_TEXT = {
    arrival: 'À l’arrivée de chaque ticket',
    arrival_some: 'À l’arrivée (Outlook EffiTrace, API, Spacefill)',
    arrival_email: 'À l’arrivée des e-mails',
    arrival_outlook: 'À l’arrivée des e-mails Outlook',
    arrival_edn: 'À l’arrivée des tickets Envoi du Net',
    agent: 'Quand l’agent l’interroge',
    schedule: 'Relue régulièrement',
    tracking: 'Suivi : arrivée des e-mails puis toutes les 12 h',
    incidents: 'À chaque incident Spacefill',
};

const LOOKUP_TEXT = {
    tracking_number: 'n° de suivi', order_number: 'n° de commande', customer_email: 'e-mail du client',
    invoice_number: 'n° de facture', client_ref: 'réf. client', client_name: 'client B2B', skus: 'références produit',
};

const STATE_TEXT = { ok: 'Active', off: 'Désactivée', failing: 'En échec', verification: 'En vérification' };

const BUILTIN_TITLES = {
    commerce_orders: 'Vos commandes (boutiques et API commandes)',
    ship24: 'Ship24 — suivi transporteur',
    edn_shipments: 'Envoi du Net — expéditions',
    spacefill: 'Spacefill',
};

const sourceTitle = (source, t) => (BUILTIN_TITLES[source.key]
    ? t(`dataSources.builtin.${source.key}`, BUILTIN_TITLES[source.key])
    : source.title || source.key);

const SourceRow = ({ source, connector, t, onOpen, onUpgraded }) => {
    const [busy, setBusy] = useState(false);
    const upgrade = async () => {
        setBusy(true);
        try {
            const url = connector.upgrade === 'effitrace'
                ? '/api/integrations/connectors/migrate/effitrace'
                : `/api/integrations/connectors/migrate/http/${encodeURIComponent(connector.key)}`;
            const res = await axios.post(url, {}, authHeaders());
            onUpgraded(res.data.key);
        } finally {
            setBusy(false);
        }
    };
    let action = null;
    if (source.editable) {
        action = <button type="button" className="tj-link" onClick={() => onOpen(source.key)}>{t('dataSources.open', 'Ouvrir')} <FaArrowRight aria-hidden="true" /></button>;
    } else if (connector?.legacy && connector?.upgrade) {
        action = <button type="button" className="tj-link" disabled={busy} onClick={upgrade}>{t('dataSources.upgrade', 'Mettre à niveau')}</button>;
    } else if (source.route) {
        action = <Link className="tj-link" to={source.route}>{t('dataSources.manage', 'Gérer')} <FaArrowRight aria-hidden="true" /></Link>;
    }
    const builtin = ['platform', 'orders', 'builtin'].includes(source.kind);
    return (
        <tr>
            <td>
                <strong className="sbc-name">{sourceTitle(source, t)}</strong>
                {builtin && <em className="tj-tag">{t('dataSources.builtinTag', 'inclus')}</em>}
                {source.kind === 'orders' && (
                    <span className="sbc-sub">{t('dataSources.ordersSub', '{{shops}} boutique(s) · {{keys}} clé(s) API commandes', { shops: source.shops || 0, keys: source.api_keys || 0 })}</span>
                )}
            </td>
            <td>
                <span className="sbc-when">
                    {(source.when || []).map((w) => <span key={w} className="sbc-chip">{t(`dataSources.when.${w}`, WHEN_TEXT[w] || w)}</span>)}
                </span>
            </td>
            <td className="sbc-lookup">{(source.lookup || []).map((l) => t(`dataSources.lookup.${l}`, LOOKUP_TEXT[l] || l)).join(', ') || '—'}</td>
            <td className="sbc-ai">{source.ai === 'repair'
                ? t('dataSources.ai.repair', 'IA seulement si la page change')
                : t('dataSources.ai.none', 'Sans IA')}</td>
            <td><span className={`tj-chip tj-chip--src-${source.state}`}>{t(`dataSources.srcState.${source.state}`, STATE_TEXT[source.state] || source.state)}</span></td>
            <td className="sbc-reads">{source.reads_7d != null ? `${source.found_7d ?? 0} / ${source.reads_7d}` : '—'}</td>
            <td className="sbc-action">{action}</td>
        </tr>
    );
};

const SourcesByCategory = ({ sources, connectors, canEdit, onAdd, onOpen, onUpgraded }) => {
    const { t } = useTranslation();
    const byKey = Object.fromEntries((connectors || []).map((c) => [c.key, c]));
    return (
        <div className="sbc">
            {CATEGORIES.map((category) => {
                const rows = (sources || []).filter((s) => (s.category || 'other') === category.key);
                if (category.key === 'other' && rows.length === 0) return null;
                const Icon = category.icon;
                return (
                    <section key={category.key} className="sbc-group">
                        <header className="sbc-group-head">
                            <span className="sbc-group-icon" aria-hidden="true"><Icon /></span>
                            <div>
                                <h3>{t(`dataSources.category.${category.key}.title`, category.title)}</h3>
                                <p>{t(`dataSources.category.${category.key}.hint`, category.hint)}</p>
                            </div>
                            {canEdit && (
                                <button type="button" className="u-btn u-btn--secondary" onClick={() => onAdd(category.key)}>
                                    <FaPlus aria-hidden="true" /> {t('dataSources.addIn', 'Ajouter')}
                                </button>
                            )}
                        </header>
                        {rows.length === 0 ? (
                            <p className="sbc-empty">{t(`dataSources.category.${category.key}.empty`, 'Aucune source de ce type.')}</p>
                        ) : (
                            <div className="sbc-table-wrap">
                                <table className="sbc-table">
                                    <thead>
                                        <tr>
                                            <th>{t('dataSources.col.source', 'Source')}</th>
                                            <th>{t('dataSources.col.when', 'Lue')}</th>
                                            <th>{t('dataSources.col.lookup', 'Recherche par')}</th>
                                            <th>{t('dataSources.col.ai', 'IA')}</th>
                                            <th>{t('dataSources.col.state', 'État')}</th>
                                            <th>{t('dataSources.col.foundReads', 'Trouvé / lu (7 j)')}</th>
                                            <th aria-label={t('dataSources.col.actions', 'Actions')} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((source) => (
                                            <SourceRow key={source.key} source={source} connector={byKey[source.key]} t={t}
                                                onOpen={onOpen} onUpgraded={onUpgraded} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default SourcesByCategory;
